import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ChefHat, CheckCircle2, History, X } from "lucide-react";
import { staffApi, alertSound, money, timeSince, STATUS_META } from "./staffApi";

const mins = (iso) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
const ageColor = (m) => (m >= 20 ? "#FF5A4E" : m >= 10 ? "#F0B429" : "#2ECC71");
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

const DoneDetail = ({ order, onClose }) => (
  <div data-testid="kitchen-done-detail" className="fixed inset-0 z-[70] bg-black/80 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#141419] rounded-3xl border border-[#D4AF37]/40 p-5 my-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-3xl font-black">TABLE {order.table}</p>
          <p className="text-white/45 text-sm font-bold mt-0.5">{order.order_id}{order.guests ? ` · ${order.guests} guests` : ""}{order.assigned_name ? ` · VeerGi: ${order.assigned_name}` : ""}</p>
        </div>
        <button data-testid="kitchen-done-detail-close" onClick={onClose} aria-label="Close"
          className="w-9 h-9 rounded-full border border-white/20 text-white/60 flex items-center justify-center"><X size={16} /></button>
      </div>
      <div className="mt-3 rounded-xl bg-black/40 p-3 divide-y divide-white/8">
        {order.items.map((i, n) => (
          <p key={n} className={`py-1.5 text-base font-bold ${i.unavailable ? "line-through opacity-40" : ""}`}>
            {i.qty}× {i.name}{i.spice ? ` · 🌶 ${i.spice}` : ""}{i.note ? ` — ${i.note}` : ""}
          </p>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/8 px-3 py-2.5">
        <span className="text-sm font-bold text-white/60">Charged (till's figure)</span>
        <span data-testid="kitchen-done-charged" className="text-xl font-black text-[#D4AF37]">
          {order.pos_total != null ? money(order.pos_total) : "Priced at the till"}
        </span>
      </div>
      {order.cleared_by && (
        <p data-testid="kitchen-done-cleared" className="mt-2 text-sm font-bold text-white/50">Table cleared by {order.cleared_by}</p>
      )}
      {(order.status_history || []).length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-black tracking-[0.25em] text-white/40">WHEN</p>
          {order.status_history.map((h, n) => (
            <p key={n} className="text-sm text-white/55 mt-1">
              {hhmm(h.ts)} · <b className="text-white/80">{(STATUS_META[h.status] || {}).label || h.status}</b>
            </p>
          ))}
        </div>
      )}
    </div>
  </div>
);

export const Kitchen = () => {
  const [tab, setTab] = useState("progress");
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [tableF, setTableF] = useState("");
  const [detail, setDetail] = useState(null);
  const [bumping, setBumping] = useState("");
  const known = useRef(null);
  const known86 = useRef(null);

  const load = useCallback(() => staffApi.get("/staff/kitchen", { params: { page, table: tableF || undefined } })
    .then((r) => {
      const active = r.data.in_progress.map((o) => o.order_id);
      if (known.current !== null && active.some((id) => !known.current.has(id))) alertSound();
      known.current = new Set(active);
      const s86 = new Set((r.data.eighty_six || []).map((d) => d.slug));
      if (known86.current !== null && [...s86].some((s) => !known86.current.has(s))) {
        alertSound();
        toast.error("86 ALERT — check the DO NOT FIRE list", { duration: 8000 });
      }
      known86.current = s86;
      setData(r.data);
    }).catch(() => {}), [page, tableF]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const bump = async (oid) => {
    setBumping(oid);
    try {
      await staffApi.post(`/staff/orders/${oid}/bump`);
      toast.success("Ticket bumped — VeerGi sees READY");
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not bump");
    }
    setBumping("");
  };

  const openDone = (oid) =>
    staffApi.get(`/staff/kitchen/orders/${oid}`).then((r) => setDetail(r.data)).catch(() => toast.error("Could not open that ticket"));

  if (!data) return <p className="p-8 text-white/50 text-xl">Loading kitchen…</p>;

  const cooking = data.in_progress;
  const pages = Math.max(1, Math.ceil(data.done_total / data.page_size));

  return (
    <div data-testid="kitchen-screen" className="p-4 pb-16 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="flex items-center gap-2.5 text-2xl font-black tracking-wide">
          <ChefHat size={26} className="text-[#D4AF37]" /> KITCHEN
        </p>
        <p className="text-white/40 text-sm font-bold">Open on the floor — no PIN needed. Kitchen taps DONE when plates are up.</p>
      </div>
      {(data.eighty_six || []).length > 0 && (
        <div data-testid="kitchen-86-strip" className="mt-4 rounded-2xl border border-[#FF5A4E]/50 bg-[#FF5A4E]/8 p-3">
          <p className="text-[11px] font-black tracking-[0.25em] text-[#FF5A4E] flex items-center gap-1.5">
            <AlertTriangle size={13} /> 86'D — DO NOT FIRE
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.eighty_six.map((d) => (
              <span key={d.slug} data-testid={`kitchen-86-${d.slug}`}
                className={`rounded-full px-3 py-1.5 text-xs font-black tracking-wide border ${
                  d.pending_pos ? "text-[#FFB020] bg-[#FFB020]/15 border-[#FFB020]/40"
                                : "text-[#FF5A4E] bg-[#FF5A4E]/15 border-[#FF5A4E]/40"}`}>
                {d.name}{d.pending_pos ? " · POS PENDING" : ""}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-2 mt-4">
        <button data-testid="kitchen-tab-progress" onClick={() => setTab("progress")}
          className={`rounded-full px-6 py-3 text-sm font-black tracking-wide border ${tab === "progress" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "s-card text-white/65"}`}>
          IN PROGRESS · {cooking.length}
        </button>
        <button data-testid="kitchen-tab-done" onClick={() => setTab("done")}
          className={`rounded-full px-6 py-3 text-sm font-black tracking-wide border flex items-center gap-1.5 ${tab === "done" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "s-card text-white/65"}`}>
          <History size={14} /> DONE · {data.done_total}
        </button>
      </div>

      {tab === "progress" && (
        <>
          {cooking.length === 0 && (
            <p data-testid="kitchen-empty" className="text-white/40 text-2xl mt-16 text-center">All caught up — nothing cooking.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
            {cooking.map((o) => {
              const m = mins(o.updated_at);
              const items = o.items.filter((i) => !i.unavailable);
              return (
                <div key={o.order_id} data-testid={`kitchen-ticket-${o.order_id}`}
                  className="s-card rounded-2xl border-2 p-4" style={{ borderColor: ageColor(m) }}>
                  <div className="flex items-start justify-between">
                    <p className="text-4xl font-black leading-none">T{o.table}</p>
                    <div className="text-right">
                      <p className="text-2xl font-black" style={{ color: ageColor(m) }}>{m} MIN</p>
                      <p className="text-[11px] text-white/40 font-bold">{o.order_id} · {o.assigned_name || "—"}</p>
                    </div>
                  </div>
                  {o.allergies && (
                    <p data-testid={`kitchen-allergy-${o.order_id}`} className="mt-2.5 flex items-center gap-2 rounded-xl bg-[#FF5A4E]/18 border border-[#FF5A4E] text-[#FF5A4E] font-black text-lg px-3 py-2">
                      <AlertTriangle size={20} /> ALLERGY: {o.allergies}
                    </p>
                  )}
                  <div className="mt-3 space-y-2">
                    {items.map((i, n) => (
                      <div key={n} className="flex items-baseline gap-3 border-b border-white/8 pb-2">
                        <span className="text-3xl font-black text-[#D4AF37] w-10 shrink-0">{i.qty}×</span>
                        <div className="min-w-0">
                          <p className="text-xl font-bold leading-tight">{i.name}</p>
                          {(i.spice || i.note) && (
                            <p className="text-base text-[#F0B429] font-bold">{i.spice ? `🌶 ${i.spice}` : ""}{i.spice && i.note ? " · " : ""}{i.note || ""}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {o.waiter_notes && <p className="mt-2 text-base font-bold text-[#5AB0FF]">VEERGI: {o.waiter_notes}</p>}
                  {o.special_instructions && <p className="mt-1 text-base font-bold text-[#F0B429]">GUEST: {o.special_instructions}</p>}
                  <button data-testid={`kitchen-bump-${o.order_id}`} onClick={() => bump(o.order_id)} disabled={bumping === o.order_id}
                    className="mt-3 w-full min-h-[64px] rounded-xl bg-[#10B981] text-black font-black text-xl tracking-wide flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#34D399]">
                    <CheckCircle2 size={24} /> {bumping === o.order_id ? "BUMPING…" : "DONE — PLATES UP"}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "done" && (
        <div className="mt-4 max-w-3xl">
          <div className="flex items-center gap-2">
            <input data-testid="kitchen-done-filter" value={tableF}
              onChange={(e) => { setTableF(e.target.value); setPage(1); }} placeholder="Filter by table — e.g. 12"
              className="flex-1 bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-base focus:outline-none focus:border-[#D4AF37]/70" />
            {tableF && (
              <button data-testid="kitchen-done-filter-clear" onClick={() => { setTableF(""); setPage(1); }}
                className="rounded-xl border border-white/20 text-white/60 font-black px-4 py-3 text-sm">CLEAR</button>
            )}
          </div>
          <p className="text-white/40 text-sm font-bold mt-2">Today's history — every finished table stays here for the day. Tap one to review what was served, what was charged and when.</p>
          {data.done.length === 0 && (
            <p data-testid="kitchen-done-empty" className="text-white/40 text-xl mt-10 text-center">Nothing finished yet{tableF ? ` for table ${tableF}` : " today"}.</p>
          )}
          <div className="space-y-2.5 mt-4">
            {data.done.map((o) => {
              const meta = STATUS_META[o.status] || STATUS_META.completed;
              return (
                <button key={o.order_id} data-testid={`kitchen-done-row-${o.order_id}`} onClick={() => openDone(o.order_id)}
                  className="w-full s-card rounded-2xl p-4 flex items-center gap-4 text-left hover:border-[#D4AF37]/40">
                  <p className="num text-3xl font-black w-20 shrink-0">T{o.table}</p>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white/70 truncate">
                      {o.order_id} · {o.items.filter((i) => !i.unavailable).length} item{o.items.filter((i) => !i.unavailable).length === 1 ? "" : "s"}
                      {o.assigned_name ? ` · ${o.assigned_name}` : ""}
                    </p>
                    <p className="text-xs text-white/40 font-bold mt-0.5">{timeSince(o.updated_at)}</p>
                  </div>
                  <span className="rounded-full px-3 py-1.5 text-[11px] font-black tracking-wider shrink-0"
                    style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
                  <span className="text-lg font-black text-[#D4AF37] w-24 text-right shrink-0">
                    {o.pos_total != null ? money(o.pos_total) : "—"}
                  </span>
                </button>
              );
            })}
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <button data-testid="kitchen-done-prev" disabled={page <= 1} onClick={() => setPage(page - 1)}
                className="rounded-full border border-white/20 text-white/70 font-black px-5 py-2.5 text-sm disabled:opacity-30">← PREV</button>
              <span data-testid="kitchen-done-page" className="text-sm font-bold text-white/50">Page {data.page} of {pages}</span>
              <button data-testid="kitchen-done-next" disabled={page >= pages} onClick={() => setPage(page + 1)}
                className="rounded-full border border-white/20 text-white/70 font-black px-5 py-2.5 text-sm disabled:opacity-30">NEXT →</button>
            </div>
          )}
        </div>
      )}

      {detail && <DoneDetail order={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};
